/**
 * WellAssignmentDialog.tsx
 * 
 * Task #93: Dialog for assigning wells to pumpers with optional reason
 * 
 * Redesigned to be mobile-friendly, opaque, and accessible using standard UI components.
 */

import { useState, useEffect } from 'react';
import { User, AlertTriangle, Info } from 'lucide-react';
import { cn } from '../ui/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Checkbox } from "../ui/checkbox";
import { Badge } from "../ui/badge";

/** Well info passed to dialog */
export interface WellForAssignment {
  id: string;
  name: string;
  currentPumperId?: string;
  currentPumperName?: string;
}

/** Pumper option for dropdown */
export interface PumperOption {
  id: string;
  name: string;
  email?: string;
  workloadScore: number;
  workloadStatus: 'light' | 'balanced' | 'heavy' | 'overloaded';
  wellCount: number;
}

/** Assignment type */
export type AssignmentType = 'primary' | 'backup';

/** Dialog result */
export interface AssignmentResult {
  pumperId: string;
  pumperName: string;
  assignmentType: AssignmentType;
  reason?: string;
  isTemporary: boolean;
  endDate?: Date;
  reminderDate?: Date;
  endAction?: 'reassign' | 'unassign' | 'notify';
}

interface WellAssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (result: AssignmentResult) => Promise<void>;
  wells: WellForAssignment[];
  pumpers: PumperOption[];
  loading?: boolean;
  defaultPumperId?: string;
  defaultAssignmentType?: AssignmentType;
}

const WORKLOAD_COLORS = {
  light: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800',
  balanced: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800',
  heavy: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 border-orange-200 dark:border-orange-800',
  overloaded: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800',
};

const WORKLOAD_LABELS = {
  light: 'Light Load',
  balanced: 'Balanced',
  heavy: 'Heavy Load',
  overloaded: 'Overloaded',
};

export default function WellAssignmentDialog({
  open,
  onClose,
  onSubmit,
  wells,
  pumpers,
  loading = false,
  defaultPumperId,
  defaultAssignmentType = 'primary',
}: WellAssignmentDialogProps) {
  // Form state
  const [selectedPumperId, setSelectedPumperId] = useState(defaultPumperId || '');
  const [assignmentType, setAssignmentType] = useState<AssignmentType>(defaultAssignmentType);
  const [reason, setReason] = useState('');
  const [isTemporary, setIsTemporary] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedPumperId(defaultPumperId || '');
      setAssignmentType(defaultAssignmentType);
      setReason('');
      setIsTemporary(false);
      setError(null);
    }
  }, [open, defaultPumperId, defaultAssignmentType]);

  const selectedPumper = pumpers.find(p => p.id === selectedPumperId);
  const isBulk = wells.length > 1;

  const handleSubmit = async () => {
    if (!selectedPumperId || !selectedPumper) {
      setError('Please select a pumper');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await onSubmit({
        pumperId: selectedPumperId,
        pumperName: selectedPumper.name,
        assignmentType,
        reason: reason.trim() || undefined,
        isTemporary,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Assignment failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(val) => !val && onClose()}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-xl">
        <DialogHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-500 dark:to-indigo-500 rounded-t-lg -mx-6 -mt-6 px-6 pt-6 pb-4">
          <DialogTitle className="text-xl font-semibold text-white">
            {isBulk ? `Assign ${wells.length} Wells` : 'Assign Well'}
          </DialogTitle>
          <DialogDescription className="text-white/90">
            Select a pumper to assign {isBulk ? 'these wells' : 'this well'} to.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4 bg-white dark:bg-slate-900">
          {/* Wells List (Compact) */}
          <div className="relative overflow-hidden rounded-2xl p-1 bg-gradient-to-br from-cyan-400/20 to-blue-400/20 border border-cyan-200 dark:border-cyan-800">
            <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur rounded-xl p-3">
              <Label className="text-xs text-cyan-700 dark:text-cyan-300 mb-2 block font-semibold">
                Selected Wells
              </Label>
              <div className="flex flex-wrap gap-2">
                {wells.slice(0, 5).map(w => (
                  <Badge key={w.id} variant="secondary" className="font-normal bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300">
                    {w.name}
                  </Badge>
                ))}
                {wells.length > 5 && (
                  <Badge variant="outline" className="text-cyan-600 dark:text-cyan-400 border-cyan-300 dark:border-cyan-700">
                    +{wells.length - 5} more
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Assignment Type */}
          <div className="space-y-3">
            <Label className="text-gray-900 dark:text-white font-medium">Assignment Type</Label>
            <RadioGroup
              value={assignmentType}
              onValueChange={(v) => setAssignmentType(v as AssignmentType)}
              className="grid grid-cols-2 gap-4"
            >
              <div>
                <RadioGroupItem value="primary" id="primary" className="sr-only" />
                <Label
                  htmlFor="primary"
                  className={cn(
                    "flex flex-col items-center justify-between rounded-md border-2 p-4 cursor-pointer transition-all",
                    "bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-700",
                    "hover:bg-gray-50 dark:hover:bg-slate-700 hover:border-gray-400 dark:hover:border-slate-600",
                    assignmentType === 'primary' ? "border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20" : ""
                  )}
                >
                  <User className="mb-2 h-6 w-6" />
                  <span className="font-semibold">Primary</span>
                  <span className="text-xs text-muted-foreground mt-1 text-center">
                    Main responsibility
                  </span>
                </Label>
              </div>
              <div>
                <RadioGroupItem value="backup" id="backup" className="sr-only" />
                <Label
                  htmlFor="backup"
                  className={cn(
                    "flex flex-col items-center justify-between rounded-md border-2 p-4 cursor-pointer transition-all",
                    "bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-700",
                    "hover:bg-gray-50 dark:hover:bg-slate-700 hover:border-gray-400 dark:hover:border-slate-600",
                    assignmentType === 'backup' ? "border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20" : ""
                  )}
                >
                  <User className="mb-2 h-6 w-6 opacity-50" />
                  <span className="font-semibold">Backup</span>
                  <span className="text-xs text-muted-foreground mt-1 text-center">
                    Coverage support
                  </span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Pumper Selection */}
          <div className="space-y-3">
            <Label className="text-gray-900 dark:text-white font-medium">Select Pumper</Label>
            <Select
              value={selectedPumperId}
              onValueChange={setSelectedPumperId}
              disabled={loading}
            >
              <SelectTrigger className="w-full h-12 bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-700 text-gray-900 dark:text-white">
                <SelectValue placeholder="Choose a pumper..." />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-700">
                {pumpers.map(pumper => (
                  <SelectItem key={pumper.id} value={pumper.id} className="py-3 hover:bg-gray-100 dark:hover:bg-slate-700">
                    <div className="flex flex-col gap-1">
                      <span className="font-medium">{pumper.name}</span>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{pumper.wellCount} wells</span>
                        <span className={cn(
                          "px-1.5 py-0.5 rounded-full text-[10px] font-medium border",
                          WORKLOAD_COLORS[pumper.workloadStatus]
                        )}>
                          {WORKLOAD_LABELS[pumper.workloadStatus]}
                        </span>
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Selected Pumper Stats */}
            {selectedPumper && (
              <div className={cn(
                "mt-2 p-3 rounded-md border text-sm flex items-start gap-3",
                WORKLOAD_COLORS[selectedPumper.workloadStatus]
              )}>
                <Info className="w-5 h-5 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">
                    Current Workload: {WORKLOAD_LABELS[selectedPumper.workloadStatus]}
                  </p>
                  <p className="opacity-90 mt-0.5">
                    {selectedPumper.name} currently manages {selectedPumper.wellCount} wells.
                    {(selectedPumper.workloadStatus === 'heavy' || selectedPumper.workloadStatus === 'overloaded') && (
                      <span className="block mt-1 font-medium">
                        ⚠️ Consider assigning to someone with more capacity.
                      </span>
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Reason & Temporary */}
          <div className="space-y-4 pt-2">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="temporary" 
                checked={isTemporary}
                onCheckedChange={(c) => setIsTemporary(!!c)}
              />
              <Label htmlFor="temporary" className="font-medium cursor-pointer text-gray-900 dark:text-white">
                This is a temporary assignment
              </Label>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reason" className="text-gray-900 dark:text-white">Assignment Reason (Optional)</Label>
              <Textarea
                id="reason"
                placeholder="e.g., Route optimization, covering for vacation..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="resize-none bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-700 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400"
                rows={3}
              />
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
              <AlertTriangle className="w-4 h-4" />
              {error}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0 bg-white dark:bg-slate-900 border-t border-gray-200 dark:border-slate-800 pt-4 -mx-6 px-6 -mb-6 pb-6 rounded-b-lg">
          <Button variant="outline" onClick={onClose} disabled={submitting} className="border-gray-300 dark:border-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !selectedPumperId}>
            {submitting ? 'Assigning...' : `Assign ${isBulk ? `${wells.length} Wells` : 'Well'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
